import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Card, Col, Container, Row, Spinner } from "react-bootstrap";
import { useParams } from "react-router-dom";
import DynamicBreadcrumb from "../header/breadcrumb";
import orderLocationApi from "../../services/orderLocationApi";
import { loadGoogleMaps } from "../../utils/googleMapsLoader";

const normalizeGeojson = (geojson) => {
    if (!geojson) return null;
    if (typeof geojson === "string") {
        try { return JSON.parse(geojson); } catch (e) { return null; }
    }
    return geojson;
};

const geojsonToPath = (geojson) => {
    const geo = normalizeGeojson(geojson);
    const ring = geo?.coordinates?.[0] || [];
    return ring.map(([lng, lat]) => ({ lat: Number(lat), lng: Number(lng) }));
};

const pointInPolygon = (latitude, longitude, coordinates = []) => {
    if (!Array.isArray(coordinates) || coordinates.length < 3) return false;

    const x = Number(longitude);
    const y = Number(latitude);
    let inside = false;

    for (let i = 0, j = coordinates.length - 1; i < coordinates.length; j = i++) {
        const xi = Number(coordinates[i][0]);
        const yi = Number(coordinates[i][1]);
        const xj = Number(coordinates[j][0]);
        const yj = Number(coordinates[j][1]);

        const intersect =
            yi > y !== yj > y &&
            x < ((xj - xi) * (y - yi)) / ((yj - yi) || 1e-12) + xi;

        if (intersect) inside = !inside;
    }

    return inside;
};

const findMatchedZoneFromAllZones = (data) => {
    if (!data?.customerLat || !data?.customerLng || !Array.isArray(data?.allZones)) return null;

    for (const zone of data.allZones) {
        const geo = normalizeGeojson(zone.geojson);
        const coordinates = geo?.coordinates?.[0] || [];
        if (pointInPolygon(data.customerLat, data.customerLng, coordinates)) {
            return zone;
        }
    }

    return null;
};

export default function OrderLocationPage() {
    const { orderId } = useParams();
    const mapDivRef = useRef(null);
    const mapRef = useRef(null);
    const polygonsRef = useRef([]);
    const markersRef = useRef([]);
    const infoWindowRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [data, setData] = useState(null);

    const effectiveMatchedZone = useMemo(() => {
        return data?.matchedZone || findMatchedZoneFromAllZones(data);
    }, [data]);

    const clearMapObjects = () => {
        polygonsRef.current.forEach((polygon) => polygon.setMap(null));
        markersRef.current.forEach((marker) => marker.setMap(null));
        polygonsRef.current = [];
        markersRef.current = [];
        if (infoWindowRef.current) infoWindowRef.current.close();
    };

    const drawMap = async (payload) => {
        const google = await loadGoogleMaps([]);
        if (!mapDivRef.current) return;

        if (!mapRef.current) {
            mapRef.current = new google.maps.Map(mapDivRef.current, {
                center: { lat: 13.0827, lng: 80.2707 },
                zoom: 11,
                mapTypeControl: true,
                streetViewControl: false,
                fullscreenControl: true,
                zoomControl: true,
            });
            infoWindowRef.current = new google.maps.InfoWindow();
        }

        clearMapObjects();

        const bounds = new google.maps.LatLngBounds();
        let hasBounds = false;
        const resolvedZone = payload?.matchedZone || findMatchedZoneFromAllZones(payload);

        (payload?.allZones || []).forEach((zone) => {
            const path = geojsonToPath(zone.geojson);
            if (!path.length) return;

            const isMatched = Number(zone.id) === Number(resolvedZone?.id);
            const isOrderBranch = Number(zone.branchId) === Number(payload?.orderBranchId);

            const polygon = new google.maps.Polygon({
                paths: path,
                map: mapRef.current,
                clickable: true,
                editable: false,
                draggable: false,
                strokeColor: isMatched ? "#111827" : (isOrderBranch ? "#dc2626" : "#2563eb"),
                fillColor: isMatched ? "#2563eb" : (isOrderBranch ? "#f87171" : "#60a5fa"),
                strokeOpacity: 0.95,
                fillOpacity: isMatched ? 0.30 : 0.10,
                strokeWeight: isMatched ? 4 : (isOrderBranch ? 3 : 2),
            });

            polygon.addListener("click", (event) => {
                infoWindowRef.current.setContent(`
          <div style="min-width:220px;line-height:1.6;">
            <div style="font-weight:700;">${zone.branchName || "-"}</div>
            <div>Branch ID: ${zone.branchId || "-"}</div>
            <div>Zone Type: ${zone.zoneType || "-"}</div>
            <div>Priority: ${zone.priority ?? "-"}</div>
            <div>Branch Lat: ${zone.branchLatitude || "-"}</div>
            <div>Branch Lng: ${zone.branchLongitude || "-"}</div>
          </div>
        `);
                infoWindowRef.current.setPosition(event.latLng);
                infoWindowRef.current.open({ map: mapRef.current });
            });

            polygonsRef.current.push(polygon);

            path.forEach((point) => {
                bounds.extend(point);
                hasBounds = true;
            });
        });

        if (payload?.customerLat && payload?.customerLng) {
            const customerMarker = new google.maps.Marker({
                position: {
                    lat: Number(payload.customerLat),
                    lng: Number(payload.customerLng),
                },
                map: mapRef.current,
                title: "Customer Location",
                label: "C",
            });

            customerMarker.addListener("click", () => {
                infoWindowRef.current.setContent(`
          <div style="min-width:220px;line-height:1.6;">
            <div style="font-weight:700;">Customer Location</div>
            <div>Lat: ${payload.customerLat}</div>
            <div>Lng: ${payload.customerLng}</div>
            <div>Order Branch: ${payload.orderBranchName || "-"}</div>
          </div>
        `);
                infoWindowRef.current.open({
                    map: mapRef.current,
                    anchor: customerMarker,
                });
            });

            markersRef.current.push(customerMarker);
            bounds.extend(customerMarker.getPosition());
            hasBounds = true;
        }

        if (payload?.branchLat && payload?.branchLng) {
            const branchMarker = new google.maps.Marker({
                position: {
                    lat: Number(payload.branchLat),
                    lng: Number(payload.branchLng),
                },
                map: mapRef.current,
                title: payload.orderBranchName || "Branch",
                label: "B",
            });

            branchMarker.addListener("click", () => {
                infoWindowRef.current.setContent(`
          <div style="min-width:220px;line-height:1.6;">
            <div style="font-weight:700;">${payload.orderBranchName || "-"}</div>
            <div>Branch ID: ${payload.orderBranchId || "-"}</div>
            <div>Lat: ${payload.branchLat || "-"}</div>
            <div>Lng: ${payload.branchLng || "-"}</div>
          </div>
        `);
                infoWindowRef.current.open({
                    map: mapRef.current,
                    anchor: branchMarker,
                });
            });

            markersRef.current.push(branchMarker);
            bounds.extend(branchMarker.getPosition());
            hasBounds = true;
        }

        if (hasBounds) {
            mapRef.current.fitBounds(bounds);
        }
    };

    useEffect(() => {
        let mounted = true;

        orderLocationApi.getOrderLocationMap(orderId)
            .then((response) => {
                if (!mounted) return;
                setData(response?.data || null);
            })
            .catch((err) => {
                if (!mounted) return;
                setError(err?.response?.data?.message || err?.message || "Failed to load order location");
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, [orderId]);

    useEffect(() => {
        if (!loading && data) {
            const timer = setTimeout(() => {
                drawMap(data);
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [loading, data]);

    return (
        <Container fluid className="py-3">
            <DynamicBreadcrumb pathArray={[
                { name: "Orders", url: "/admin/orders/pending" },
                { name: "Order Location", url: null },
            ]} />

            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" /></div>
            ) : error ? (
                <Alert variant="danger">{error}</Alert>
            ) : (
                <Row className="g-3">
                    <Col md={8}>
                        <div ref={mapDivRef} style={{ width: "100%", height: 700, borderRadius: 12, overflow: "hidden" }} />
                    </Col>
                    <Col md={4}>
                        <Card className="mb-3">
                            <Card.Body>
                                <h5>Order Info</h5>
                                <div><strong>Order:</strong> {data?.orderShowId}</div>
                                <div><strong>Customer:</strong> {data?.customerName}</div>
                                <div><strong>Branch:</strong> {data?.orderBranchName}</div>
                                <div><strong>Address:</strong> {data?.shippingAddress}</div>
                            </Card.Body>
                        </Card>

                        <Card className="mb-3">
                            <Card.Body>
                                <h5>Coordinates</h5>
                                <div><strong>Customer Lat:</strong> {data?.customerLat}</div>
                                <div><strong>Customer Lng:</strong> {data?.customerLng}</div>
                                <div><strong>Branch Lat:</strong> {data?.branchLat}</div>
                                <div><strong>Branch Lng:</strong> {data?.branchLng}</div>
                            </Card.Body>
                        </Card>

                        <Card>
                            <Card.Body>
                                <h5>Zone Match</h5>
                                {effectiveMatchedZone ? (
                                    <>
                                        <div className="text-success fw-bold mb-2">Inside Zone</div>
                                        <div><strong>Matched Branch:</strong> {effectiveMatchedZone.branchName}</div>
                                        <div><strong>Matched Branch ID:</strong> {effectiveMatchedZone.branchId}</div>
                                        <div><strong>Zone Type:</strong> {effectiveMatchedZone.zoneType}</div>
                                        <div><strong>Priority:</strong> {effectiveMatchedZone.priority}</div>
                                    </>
                                ) : (
                                    <div className="text-danger fw-bold">Outside All Zones</div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}
        </Container>
    );
}